var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { AlertCircleIcon, AlertTriangleIcon, CheckCircleIcon, FileTextIcon, InfoIcon, XIcon } from '../icon';
import { IconButton, iconButtonClasses } from '../icon-button';
import { buttonClasses } from '../button';
import { composeClasses } from '../styles/classes';
import { styled } from '../styled';
import clsx from '../utils/clsx';
import { alertClasses, alertComponentName, getAlertUtilityClass } from './alert-classes';
var severityBorderShade = {
    default: 'color_bg_state_neutral',
    danger: 'color_bg_state_danger',
    warning: 'color_bg_state_caution',
    info: 'color_bg_state_info',
    success: 'color_bg_state_success',
};
var severityIconShade = {
    default: 'color_fg_state_neutral',
    danger: 'color_fg_state_danger',
    warning: 'color_fg_state_caution',
    info: 'color_fg_state_info',
    success: 'color_fg_state_success',
};
var severityBackgroundShade = {
    default: 'color_bg_state_neutral_subtle',
    danger: 'color_bg_state_danger_subtle',
    warning: 'color_bg_state_caution_subtle',
    info: 'color_bg_state_info_subtle',
    success: 'color_bg_state_success_subtle',
};
var severityColorSubtleShade = {
    default: 'color_fg_on_state_neutral_subtle',
    danger: 'color_fg_on_state_danger_subtle',
    warning: 'color_fg_on_state_caution_subtle',
    info: 'color_fg_on_state_info_subtle',
    success: 'color_fg_on_state_success_subtle',
};
var AlertRoot = styled('div', {
    name: alertComponentName,
    slot: 'Root',
})(function (_a) {
    var _b, _c;
    var theme = _a.theme, ownerState = _a.ownerState;
    return __assign(__assign({ display: 'flex', alignItems: 'flex-start', borderRadius: theme.shape.borderRadius, padding: theme.spacing(1.25, 2), boxShadow: ownerState.elevation ? theme.shadows[ownerState.elevation] : 'none', borderLeft: "".concat(theme.typography.pxToRem(8), " solid ").concat(theme.tokens[severityBorderShade[ownerState.severity]]) }, (ownerState.variant === 'outlined' && (_b = {
            backgroundColor: theme.tokens.color_bg_layer,
            borderTop: "1px solid ".concat(theme.tokens.color_border_bold),
            borderRight: "1px solid ".concat(theme.tokens.color_border_bold),
            borderBottom: "1px solid ".concat(theme.tokens.color_border_bold)
        },
        _b["& .".concat(alertClasses.icon)] = {
            color: theme.tokens[severityIconShade[ownerState.severity]],
        },
        _b))), (ownerState.variant === 'standard' && (_c = {
            backgroundColor: theme.tokens[severityBackgroundShade[ownerState.severity]],
            color: theme.tokens[severityColorSubtleShade[ownerState.severity]],
            '& a,& a:hover': {
                color: 'currentColor',
                textDecoration: 'underline',
            }
        },
        _c["& .".concat(alertClasses.icon)] = {
            color: theme.tokens[severityColorSubtleShade[ownerState.severity]],
        },
        _c)));
});
var AlertIcon = styled('div', {
    name: alertComponentName,
    slot: 'Icon',
})(function (_a) {
    var theme = _a.theme;
    return ({
        fontSize: theme.typography.pxToRem(20),
        marginRight: theme.spacing(1.5),
        lineHeight: 1,
        paddingTop: theme.typography.pxToRem(5),
        '& svg': {
            height: '1em',
            width: '1em',
        },
    });
});
var AlertContent = styled('div', {
    name: alertComponentName,
    slot: 'Content',
})(function (_a) {
    var theme = _a.theme;
    return (__assign(__assign({}, theme.typography.body2), { flex: 1, paddingTop: theme.typography.pxToRem(4) }));
});
var AlertTitle = styled('div', {
    name: alertComponentName,
    slot: 'Title',
})(function (_a) {
    var theme = _a.theme;
    return ({
        fontWeight: theme.typography.fontWeightMedium,
        display: 'inline',
        marginRight: theme.spacing(0.5),
    });
});
var AlertMessage = styled('div', {
    name: alertComponentName,
    slot: 'Message',
})(function (_a) {
    var theme = _a.theme;
    return ({
        display: 'inline',
        '& > ul': {
            listStyle: 'disc',
            marginTop: theme.spacing(0.5),
            paddingLeft: theme.spacing(2),
        },
    });
});
var AlertAction = styled('div', {
    name: alertComponentName,
    slot: 'Action',
})(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            marginLeft: theme.spacing(1.5),
            marginRight: theme.spacing(-1)
        },
        _b["& .".concat(buttonClasses.outlined, ", & .").concat(iconButtonClasses.colorInherit)] = {
            backgroundColor: 'transparent',
            '&:hover': {
                backgroundColor: theme.mixins.getColorModeValue({
                    light: "rgba(0,0,0,".concat(theme.palette.action.hoverOpacity, ")"),
                    dark: "rgba(255,255,255,".concat(theme.palette.action.hoverOpacity, ")"),
                }),
            },
        },
        _b);
});
var defaultIconMapping = {
    warning: React.createElement(AlertTriangleIcon, null),
    danger: React.createElement(AlertCircleIcon, null),
    success: React.createElement(CheckCircleIcon, null),
    info: React.createElement(InfoIcon, null),
    default: React.createElement(FileTextIcon, null),
};
var useUtilityClasses = function (_a) {
    var classes = _a.classes;
    var slots = {
        root: ['root'],
        icon: ['icon'],
        message: ['message'],
        action: ['action'],
        title: ['title'],
        content: ['content'],
    };
    return composeClasses(slots, getAlertUtilityClass, classes);
};
export var Alert = React.forwardRef(function (props, ref) {
    // const props = useThemeProps({ props: inProps, name: alertComponentName });
    var classes = useUtilityClasses(props);
    var action = props.action, children = props.children, className = props.className, _a = props.closeText, closeText = _a === void 0 ? 'Dismiss' : _a, iconProp = props.icon, _b = props.iconMapping, iconMapping = _b === void 0 ? defaultIconMapping : _b, message = props.message, onCloseProp = props.onClose, onDismiss = props.onDismiss, _c = props.role, role = _c === void 0 ? 'alert' : _c, _d = props.severity, severity = _d === void 0 ? 'default' : _d, title = props.title, _e = props.variant, variant = _e === void 0 ? 'standard' : _e, elevation = props.elevation, dismissible = props.dismissible, rootProps = __rest(props, ["action", "children", "className", "closeText", "icon", "iconMapping", "message", "onClose", "onDismiss", "role", "severity", "title", "variant", "elevation", "dismissible"]);
    var ownerState = {
        severity: severity,
        variant: variant,
        elevation: elevation,
    };
    var icon = iconProp != null ? iconProp : iconMapping[severity];
    var onClose = onCloseProp || onDismiss;
    var showCloseButton = !!(dismissible && onClose);
    var hasAction = !!(action || showCloseButton);
    return (React.createElement(AlertRoot, __assign({ ref: ref }, rootProps, { role: role, ownerState: ownerState, className: clsx(classes.root, className) }),
        React.createElement(AlertIcon, { className: classes.icon }, icon),
        React.createElement(AlertContent, { className: classes.content },
            !!title && React.createElement(AlertTitle, { className: classes.title }, title),
            React.createElement(AlertMessage, { className: classes.message }, message || children)),
        hasAction && (React.createElement(AlertAction, { className: classes.action }, action ||
            (showCloseButton && (React.createElement(IconButton, { color: "inherit", size: "small", variant: "link", "aria-label": closeText, onClick: onClose },
                React.createElement(XIcon, null))))))));
});
